import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ChevronLeft,
  Award,
  DollarSign,
  Clock,
  TrendingDown,
  Check,
  Star,
  Ship,
  Plane,
  Truck,
  Train,
  AlertTriangle,
  History,
  MessageSquare,
  Lightbulb,
  CheckCircle2,
  Shield,
  Zap,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { PANEL } from '@/lib/surface';
import { Badge } from '@/components/ui/badge';
import { PageHeader, KPIStrip, DataTable, Column } from '@/components/common';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { MetricCard } from '@/components/common';
import {
  QuoteVersionHistoryDialog,
  StartNegotiationDialog,
  NegotiationPanel,
} from '@/components/tms';
import {
  rfqApi,
  FreightRFQ,
  FreightRFQLine,
  FreightRFQResponse,
  ShipmentType,
} from '@/api/services';
import { getErrorMessage } from '@/api/client';
import { stagger, fadeUp } from '@/lib/animations';

const shipmentTypeIcons: Record<ShipmentType, React.ReactNode> = {
  OCEAN_FCL: <Ship className="w-5 h-5" />,
  OCEAN_LCL: <Ship className="w-5 h-5" />,
  AIR: <Plane className="w-5 h-5" />,
  FTL: <Truck className="w-5 h-5" />,
  LTL: <Truck className="w-5 h-5" />,
  RAIL: <Train className="w-5 h-5" />,
  BREAK_BULK: <Ship className="w-5 h-5" />,
  INTERMODAL: <Truck className="w-5 h-5" />,
};

const statusColors: Record<string, string> = {
  Draft: 'bg-muted text-muted-foreground border-border',
  Open: 'bg-primary-50 text-primary-600 border-primary-200',
  Closed: 'bg-warning-50 text-warning-600 border-warning-200',
  Awarded: 'bg-success-50 text-success-600 border-success-200',
  Cancelled: 'bg-danger-50 text-danger-500 border-danger-200',
};

export default function FreightRFQCompare() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [rfq, setRfq] = useState<FreightRFQ | null>(null);
  const [lines, setLines] = useState<FreightRFQLine[]>([]);
  const [responses, setResponses] = useState<FreightRFQResponse[]>([]);
  const [selectedLineId, setSelectedLineId] = useState<string | null>(null);
  const [awarding, setAwarding] = useState<string | null>(null);

  // Quote Version History Dialog
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [historyCarrierId, setHistoryCarrierId] = useState<string | null>(null);

  // Negotiation Dialog and Panel
  const [negotiationDialogOpen, setNegotiationDialogOpen] = useState(false);
  const [negotiationPanelOpen, setNegotiationPanelOpen] = useState(false);
  const [negotiatingResponse, setNegotiatingResponse] = useState<FreightRFQResponse | null>(null);

  const loadRFQ = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const data = await rfqApi.getById(id);
      setRfq(data);
      setLines(data.lines || []);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      loadRFQ();
    }
  }, [id, loadRFQ]);

  useEffect(() => {
    const lineParam = searchParams.get('line');
    if (lineParam && lines.some((l) => l.id === lineParam)) {
      setSelectedLineId(lineParam);
    } else if (lines.length > 0 && !selectedLineId) {
      setSelectedLineId(lines[0].id);
    }
  }, [lines, searchParams, selectedLineId]);

  const loadLineResponses = useCallback(
    async (lineId: string) => {
      if (!id) return;
      try {
        const data = await rfqApi.compareQuotes(id, lineId);
        const sorted = [...data.responses].sort(
          (a, b) => (a.totalRate || a.baseRate) - (b.totalRate || b.baseRate)
        );
        setResponses(sorted);
      } catch (error) {
        console.error('Failed to load responses:', error);
        setResponses([]);
      }
    },
    [id]
  );

  useEffect(() => {
    if (selectedLineId) {
      loadLineResponses(selectedLineId);
      setSearchParams({ line: selectedLineId });
    }
  }, [selectedLineId, loadLineResponses, setSearchParams]);

  const handleAwardLine = async (responseId: string) => {
    if (!id || !selectedLineId) return;
    try {
      setAwarding(responseId);
      await rfqApi.createAward(id, {
        lineId: selectedLineId,
        responseId,
        awardedRate: responses.find((r) => r.id === responseId)?.totalRate || 0,
      });
      toast.success('Line awarded successfully');
      await loadRFQ();
      await loadLineResponses(selectedLineId);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setAwarding(null);
    }
  };

  const selectedLine = lines.find((l) => l.id === selectedLineId);
  const lowestRate =
    responses.length > 0 ? Math.min(...responses.map((r) => r.totalRate || r.baseRate)) : null;
  const targetRate = selectedLine?.targetRate;
  /** Percent under target. Negative means the best quote came in over it. */
  const targetVariance =
    targetRate && lowestRate ? ((targetRate - lowestRate) / targetRate) * 100 : null;

  /**
   * Quotes with their per-row derived values resolved up front.
   *
   * The comparison used to compute rank, totals and the lowest/awarded flags
   * inline while mapping rows. A column's render receives only the row, not
   * its index, so those values live on the row now — which also makes them
   * real fields, so sorting and CSV export see the totals rather than the
   * unpriced quote.
   */
  const comparisonRows = responses.map((response, index) => {
    const totalRate = response.totalRate || response.baseRate;
    const childRatesTotal = response.childRates?.reduce((sum, cr) => sum + cr.amount, 0) || 0;
    return {
      ...response,
      rank: index + 1,
      totalRate,
      childRatesTotal,
      isLowest: totalRate === lowestRate,
      isBelowTarget: !!targetRate && totalRate < targetRate,
      isAwarded: selectedLine?.award?.responseId === response.id,
      carrierName: response.invitation?.carrier?.name || 'Unknown',
    };
  });
  type ComparisonRow = (typeof comparisonRows)[number];

  const comparisonColumns: Column<ComparisonRow>[] = [
    {
      key: 'rank',
      header: 'Rank',
      sortable: true,
      render: (r) => (
        <span
          className={cn('font-medium', r.rank === 1 ? 'text-success-600' : 'text-muted-foreground')}
        >
          #{r.rank}
        </span>
      ),
    },
    {
      key: 'carrierName',
      header: 'Carrier',
      sortable: true,
      render: (r) => (
        <div className="space-y-1">
          <div className="font-medium">{r.carrierName}</div>
          <div className="text-xs leading-4 text-muted-foreground">
            {r.invitation?.carrier?.code}
          </div>
        </div>
      ),
    },
    {
      key: 'baseRate',
      header: 'Base Rate',
      sortable: true,
      className: 'text-right',
      render: (r) => (
        <>
          <span className="font-medium">${r.baseRate.toLocaleString()}</span>
          <div className="text-xs text-muted-foreground">{r.currency || 'USD'}</div>
        </>
      ),
    },
    {
      key: 'childRatesTotal',
      header: 'Child Rates',
      sortable: true,
      className: 'text-right',
      render: (r) =>
        r.childRatesTotal > 0 ? (
          <div>
            <span className="font-medium">+${r.childRatesTotal.toLocaleString()}</span>
            <div className="text-xs text-muted-foreground">{r.childRates?.length} charge(s)</div>
          </div>
        ) : (
          <span className="text-muted-foreground">-</span>
        ),
    },
    {
      key: 'totalRate',
      header: 'Total Rate',
      sortable: true,
      className: 'text-right',
      render: (r) => (
        <>
          <div className="flex items-center justify-end gap-2">
            <span
              className={cn('font-bold text-lg', r.isLowest && 'text-status-success-foreground')}
            >
              ${r.totalRate.toLocaleString()}
            </span>
          </div>
          {targetRate && (
            <div
              className={cn(
                'text-xs',
                r.isBelowTarget
                  ? 'text-status-success-foreground'
                  : 'text-status-warning-foreground'
              )}
            >
              {r.isBelowTarget ? '-' : '+'}
              {Math.abs(((r.totalRate - targetRate) / targetRate) * 100).toFixed(1)}% vs target
            </div>
          )}
        </>
      ),
    },
    {
      key: 'transitDaysOffered',
      header: 'Transit Days',
      sortable: true,
      className: 'text-center',
      render: (r) =>
        r.transitDaysOffered ? (
          <div className="flex items-center justify-center gap-1">
            <span className="font-medium">{r.transitDaysOffered}</span>
            <span className="text-sm text-muted-foreground">days</span>
          </div>
        ) : (
          <span className="text-muted-foreground">-</span>
        ),
    },
    {
      key: 'quoteStatus',
      header: 'Status',
      sortable: true,
      render: (r) => (
        <div className="flex flex-col gap-1">
          <Badge
            variant="outline"
            className={
              r.quoteStatus === 'Submitted'
                ? 'bg-primary-50 text-primary-600 border-primary-200'
                : r.quoteStatus === 'Accepted'
                  ? 'bg-success-50 text-success-600 border-success-200'
                  : ''
            }
          >
            {r.quoteStatus}
          </Badge>
          {r.isAwarded && (
            <Badge className="bg-success-100 text-success-600 border-success-200">Awarded</Badge>
          )}
          {r.isContractRate && (
            <Badge variant="outline" className="bg-purple-50 text-purple-600 border-purple-200">
              Contract Rate
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      className: 'text-center',
      render: (r) => (
        <div className="flex items-center justify-center gap-2">
          <Button
            aria-label="View quote history"
            variant="ghost"
            size="icon"
            title="View quote history"
            onClick={() => {
              setHistoryCarrierId(r.invitation?.carrier?.id || null);
              setHistoryDialogOpen(true);
            }}
          >
            <History className="w-4 h-4" />
          </Button>
          <Button
            aria-label="Negotiate rate"
            variant="ghost"
            size="icon"
            title="Negotiate rate"
            onClick={() => {
              setNegotiatingResponse(r);
              setNegotiationDialogOpen(true);
            }}
            disabled={!!selectedLine?.award || rfq?.status === 'Awarded'}
          >
            <MessageSquare className="w-4 h-4" />
          </Button>
          {!selectedLine?.award && rfq?.status !== 'Awarded' && (
            <Button size="sm" onClick={() => handleAwardLine(r.id)} disabled={awarding === r.id}>
              {awarding === r.id ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <>
                  <Award className="w-4 h-4 mr-1" />
                  Award
                </>
              )}
            </Button>
          )}
          {r.isAwarded && (
            <span className="text-sm text-status-success-foreground font-medium">Awarded</span>
          )}
        </div>
      ),
    },
  ];

  // Generate L1 explanation
  const getL1Explanation = () => {
    if (responses.length === 0) return null;

    const l1Response = responses[0];
    const l1Total = l1Response.totalRate || l1Response.baseRate;
    const l1Carrier = l1Response.invitation?.carrier;
    const reasons: {
      icon: React.ReactNode;
      title: string;
      detail: string;
      highlight?: boolean;
    }[] = [];

    if (responses.length > 1) {
      const secondLowest = responses[1].totalRate || responses[1].baseRate;
      const savings = secondLowest - l1Total;
      const savingsPercent = ((savings / secondLowest) * 100).toFixed(1);
      reasons.push({
        icon: <DollarSign className="w-4 h-4" />,
        title: 'Best Value',
        detail: `Lowest total cost at $${l1Total.toLocaleString()} - saves $${savings.toLocaleString()} (${savingsPercent}%) vs next bid`,
        highlight: true,
      });
    } else {
      reasons.push({
        icon: <DollarSign className="w-4 h-4" />,
        title: 'Only Quote',
        detail: `Submitted at $${l1Total.toLocaleString()}`,
      });
    }

    if (targetRate && l1Total < targetRate) {
      const savingsVsTarget = (((targetRate - l1Total) / targetRate) * 100).toFixed(1);
      reasons.push({
        icon: <TrendingDown className="w-4 h-4" />,
        title: 'Below Target',
        detail: `${savingsVsTarget}% under your target rate of $${targetRate.toLocaleString()}`,
        highlight: true,
      });
    }

    const minTransit = Math.min(
      ...responses.filter((r) => r.transitDaysOffered).map((r) => r.transitDaysOffered!)
    );
    if (
      l1Response.transitDaysOffered &&
      l1Response.transitDaysOffered === minTransit &&
      responses.length > 1
    ) {
      reasons.push({
        icon: <Zap className="w-4 h-4" />,
        title: 'Fastest Transit',
        detail: `${l1Response.transitDaysOffered} days transit - quickest among all quotes`,
      });
    } else if (l1Response.transitDaysOffered) {
      reasons.push({
        icon: <Clock className="w-4 h-4" />,
        title: 'Transit Time',
        detail: `${l1Response.transitDaysOffered} days transit offered`,
      });
    }

    if (l1Response.isContractRate) {
      reasons.push({
        icon: <Shield className="w-4 h-4" />,
        title: 'Contract Rate',
        detail: 'Pre-negotiated contract rate with stable pricing guarantees',
      });
    }

    const hasChildRates = l1Response.childRates && l1Response.childRates.length > 0;
    if (hasChildRates) {
      reasons.push({
        icon: <CheckCircle2 className="w-4 h-4" />,
        title: 'Transparent Pricing',
        detail: `All ${l1Response.childRates!.length} accessorial charges itemized for full cost visibility`,
      });
    }

    return {
      carrier: l1Carrier?.name || 'Unknown',
      carrierCode: l1Carrier?.code,
      totalRate: l1Total,
      reasons,
    };
  };

  const l1Explanation = getL1Explanation();

  if (loading || !rfq) {
    return (
      <div className="space-y-5">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={() => navigate(`/tms/rfq/${id}`)}>
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back to RFQ
          </Button>
        </div>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <motion.main className="space-y-5" variants={stagger} initial="hidden" animate="show">
      {/* Header */}
      <motion.div variants={fadeUp}>
        <PageHeader
          backLink={{ label: 'Back to Freight RFQ', href: '/tms/rfq' }}
          title="Compare RFQs"
          description={`${rfq.rfqNumber} - ${rfq.title}`}
          actions={
            <div className="flex items-center gap-3">
              <span
                className={`px-2 py-0.5 rounded-full text-xs font-medium border ${statusColors[rfq.status]}`}
              >
                {rfq.status}
              </span>
              {rfq.shipmentType && (
                <span className="flex items-center gap-1 px-2 py-0.5 bg-muted rounded text-xs">
                  {shipmentTypeIcons[rfq.shipmentType]}
                  {rfq.shipmentType.replace('_', ' ')}
                </span>
              )}
              <Button variant="outline" onClick={() => setNegotiationPanelOpen(true)}>
                <MessageSquare className="w-4 h-4 mr-2" />
                View Negotiations
              </Button>
            </div>
          }
        />
      </motion.div>

      {/* Lane Selector */}
      <motion.div variants={fadeUp}>
        <div className={cn(PANEL, 'p-4')}>
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium">Select Lane:</label>
            <Select value={selectedLineId || ''} onValueChange={setSelectedLineId}>
              <SelectTrigger className="w-full max-w-md">
                <SelectValue placeholder="Select a lane" />
              </SelectTrigger>
              <SelectContent>
                {lines.map((line) => (
                  <SelectItem key={line.id} value={line.id}>
                    Lane #{line.lineNumber}: {line.originCity}, {line.originState}{' '}
                    {line.originPort && `(${line.originPort})`} → {line.destCity}, {line.destState}{' '}
                    {line.destPort && `(${line.destPort})`}
                    {line.award && ' [Awarded]'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </motion.div>

      {/* KPIs */}
      {selectedLine && (
        <div>
          <KPIStrip columns={4}>
            <MetricCard
              label="Quotes Received"
              value={responses.length}
              sentiment="info"
              icon={<DollarSign className="w-5 h-5" />}
            />
            <MetricCard
              label="Lowest Quote"
              value={lowestRate ? `$${lowestRate.toLocaleString()}` : '-'}
              sentiment="success"
              icon={<TrendingDown className="w-5 h-5" />}
            />
            <MetricCard
              label="Target Rate"
              value={targetRate ? `$${targetRate.toLocaleString()}` : '-'}
              sentiment="neutral"
              icon={<Star className="w-5 h-5" />}
            />
            {/* Reads as a saving only when the quote actually beats the
                target. The sentiment and icon used to be hardcoded to success,
                so a lowest quote of $1,100 against a $700 target reported a
                green "-57.1%" — a 57% overrun presented as money saved. */}
            <MetricCard
              label={
                targetVariance !== null && targetVariance < 0 ? 'Over Target' : 'Savings vs Target'
              }
              value={targetVariance === null ? '-' : `${Math.abs(targetVariance).toFixed(1)}%`}
              sentiment={
                targetVariance === null ? 'neutral' : targetVariance < 0 ? 'danger' : 'success'
              }
              icon={
                targetVariance !== null && targetVariance < 0 ? (
                  <AlertTriangle className="w-5 h-5" />
                ) : (
                  <Check className="w-5 h-5" />
                )
              }
            />
          </KPIStrip>
        </div>
      )}

      {/* Why L1 Explainer */}
      {l1Explanation && responses.length > 0 && !selectedLine?.award && (
        <div className="bg-gradient-to-r from-success-50 to-success-100 rounded-xl border border-success-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-success-200 bg-success-100">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-success-500 text-white">
                <Lightbulb className="w-4 h-4" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-success-900 flex items-center gap-2">
                  Why {l1Explanation.carrier} is ranked L1
                  <Badge className="bg-success-500 text-white hover:bg-success-600">
                    <Star className="w-3 h-3 mr-1" />
                    Recommended
                  </Badge>
                </h3>
                <p className="text-sm text-success-700">
                  Best value at ${l1Explanation.totalRate.toLocaleString()} for this lane
                </p>
              </div>
            </div>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {l1Explanation.reasons.map((reason, idx) => (
                <div
                  key={idx}
                  className={`flex items-start gap-3 p-3 rounded-lg ${
                    reason.highlight ? 'bg-success-100 border border-success-200' : 'bg-card border'
                  }`}
                >
                  <div
                    className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                      reason.highlight
                        ? 'bg-success-500 text-white'
                        : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {reason.icon}
                  </div>
                  <div>
                    <div className={`font-medium ${reason.highlight ? 'text-success-900' : ''}`}>
                      {reason.title}
                    </div>
                    <div
                      className={`text-sm ${reason.highlight ? 'text-success-700' : 'text-muted-foreground'}`}
                    >
                      {reason.detail}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 pt-4 border-t border-success-200 flex items-center justify-between">
              <p className="text-sm text-success-700">
                <strong>Recommendation:</strong> Award this bid to maximize savings while meeting
                transit requirements.
              </p>
              {!selectedLine?.award && rfq.status !== 'Awarded' && (
                <Button
                  onClick={() => handleAwardLine(responses[0].id)}
                  disabled={awarding === responses[0].id}
                  className="bg-success-600 hover:bg-success-700"
                >
                  {awarding === responses[0].id ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  ) : (
                    <>
                      <Award className="w-4 h-4 mr-2" />
                      Award to {l1Explanation.carrier}
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Comparison Table */}
      <div className={cn(PANEL, 'overflow-hidden')}>
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-semibold">
            Quote Comparison - Lane #{selectedLine?.lineNumber}
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            {selectedLine?.originCity} → {selectedLine?.destCity} • {responses.length} quote(s)
          </p>
        </div>

        {responses.length > 0 ? (
          <div className="overflow-x-auto">
            <DataTable
              pagination={false}
              columns={comparisonColumns}
              data={comparisonRows}
              rowClassName={(row) =>
                cn(
                  row.isAwarded && 'bg-primary/5',
                  // Solid shade, not /50: the numbered scales are bare var()s
                  // holding hex, so Tailwind cannot inject an alpha and the
                  // opacity modifier compiles away to transparent.
                  !row.isAwarded && row.isLowest && 'bg-success-50'
                )
              }
            />
          </div>
        ) : (
          <div className="p-12 text-center">
            <AlertTriangle className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" />
            <h3 className="text-base font-semibold mb-2">No Quotes Received</h3>
            <p className="text-muted-foreground">
              No carriers have submitted quotes for this lane yet.
            </p>
          </div>
        )}
      </div>

      {/* Lane Summary */}
      <div className={cn(PANEL, 'p-6')}>
        <h2 className="text-lg font-semibold mb-4">All Lanes Summary</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {lines.map((line) => {
            const isSelected = line.id === selectedLineId;
            return (
              <button
                key={line.id}
                onClick={() => setSelectedLineId(line.id)}
                className={`text-left p-4 rounded-lg border-2 transition-all ${
                  isSelected
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-muted-foreground/30'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium">Lane #{line.lineNumber}</span>
                  {line.award && (
                    <Badge className="bg-success-100 text-success-600">
                      <Award className="w-3 h-3 mr-1" />
                      Awarded
                    </Badge>
                  )}
                </div>
                <div className="text-sm text-muted-foreground">
                  {line.originCity} → {line.destCity}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {line.mode} • {line.responses?.length || 0} quotes
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Quote Version History Dialog */}
      {id && selectedLineId && historyCarrierId && (
        <QuoteVersionHistoryDialog
          open={historyDialogOpen}
          onOpenChange={setHistoryDialogOpen}
          rfqId={id}
          lineId={selectedLineId}
          carrierId={historyCarrierId}
          lineInfo={
            selectedLine && selectedLine.originCity && selectedLine.destCity
              ? {
                  lineNumber: selectedLine.lineNumber,
                  originCity: selectedLine.originCity,
                  destCity: selectedLine.destCity,
                }
              : undefined
          }
        />
      )}

      {/* Start Negotiation Dialog */}
      {id && selectedLineId && negotiatingResponse && (
        <StartNegotiationDialog
          open={negotiationDialogOpen}
          onOpenChange={(open) => {
            setNegotiationDialogOpen(open);
            if (!open) setNegotiatingResponse(null);
          }}
          rfqId={id}
          lineId={selectedLineId}
          responseId={negotiatingResponse.id}
          carrierName={negotiatingResponse.invitation?.carrier?.name || 'Unknown Carrier'}
          currentRate={negotiatingResponse.totalRate || negotiatingResponse.baseRate}
          currency={negotiatingResponse.currency || 'USD'}
          onSuccess={() => {
            loadLineResponses(selectedLineId);
          }}
        />
      )}

      {/* Negotiation Panel */}
      {id && (
        <NegotiationPanel
          open={negotiationPanelOpen}
          onOpenChange={setNegotiationPanelOpen}
          rfqId={id}
          onNegotiationUpdate={() => {
            if (selectedLineId) {
              loadLineResponses(selectedLineId);
            }
          }}
        />
      )}
    </motion.main>
  );
}
